import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiActivity, FiCheckCircle, FiLogOut, FiSearch, FiXCircle } from "react-icons/fi";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

const DERIV_WS_URL = "wss://api.derivws.com/trading/v1/options/ws/public";
const VOLATILITY_SYMBOL_PATTERN = /^(R_\d+|1HZ\d+V)$/;

const scaleConfidence = (raw) => Math.round(85 + (raw / 100) * 14);

function fetchVolatilityMarkets(onMarkets, onTick) {
  const ws = new WebSocket(DERIV_WS_URL);

  ws.onopen = () => {
    ws.send(JSON.stringify({ active_symbols: "brief", req_id: 1 }));
  };

  ws.onmessage = (e) => {
    const res = JSON.parse(e.data);

    if (res.msg_type === "active_symbols" && Array.isArray(res.active_symbols)) {
      const markets = res.active_symbols
        .filter(
          (s) =>
            VOLATILITY_SYMBOL_PATTERN.test(s.underlying_symbol) &&
            s.exchange_is_open &&
            !s.is_trading_suspended
        )
        .map((s) => ({ symbol: s.underlying_symbol, name: s.underlying_symbol_name }))
        .sort((a, b) => a.symbol.localeCompare(b.symbol));

      onMarkets(markets);
      markets.forEach(({ symbol }) => ws.send(JSON.stringify({ ticks: symbol, subscribe: 1 })));
      return;
    }

    if (res.tick?.quote !== undefined) {
      onTick(res.tick.symbol, res.tick.quote);
    }
  };

  ws.onerror = (err) => console.error("Deriv WS error:", err);

  return ws;
}

const PRODUCTS = [
  { key: "overUnder", label: "Over/Under", price: "$70" },
  { key: "evenOdd", label: "Even/Odd", price: "$100" },
  { key: "matches", label: "Matches", price: "$200" },
];

const hasAnyApproval = (u) =>
  u.approved || u.approvals?.matches || u.approvals?.overUnder || u.approvals?.evenOdd;

const isProductApproved = (u, product) => {
  if (product === "matches") return u.approved || u.approvals?.matches;
  return u.approvals?.[product] === true;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [adminName, setAdminName] = useState("Admin");

  useEffect(() => {
    const adminInfo = JSON.parse(localStorage.getItem("adminInfo") || "{}");
    if (!adminInfo?.email) {
      navigate("/admin/login", { replace: true });
      return;
    }
    setAdminName(adminInfo.name || adminInfo.email.split("@")[0]);
  }, [navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, "deriv_users")).catch((err) => {
          console.error("Fetch users error:", err);
          throw new Error("Failed to fetch users. Please check Firestore security rules.");
        });
        const allUsers = querySnapshot.docs.map((docSnap) => ({ uid: docSnap.id, ...docSnap.data() }));
        setUsers(allUsers);
      } catch (err) {
        console.error("Fetch users error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const pendingUsers = useMemo(
    () => users.filter((u) => !hasAnyApproval(u) && u.status !== "rejected"),
    [users]
  );

  const approvedUsers = useMemo(
    () => users.filter((u) => hasAnyApproval(u)),
    [users]
  );

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.loginid?.toLowerCase().includes(term)
    );
  }, [searchTerm, users]);

  const filteredPendingUsers = useMemo(() => {
    if (!searchTerm.trim()) return pendingUsers;
    const term = searchTerm.toLowerCase();
    return pendingUsers.filter(
      (u) =>
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.loginid?.toLowerCase().includes(term)
    );
  }, [pendingUsers, searchTerm]);

  const filteredApprovedUsers = useMemo(() => {
    if (!searchTerm.trim()) return approvedUsers;
    const term = searchTerm.toLowerCase();
    return approvedUsers.filter(
      (u) =>
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.loginid?.toLowerCase().includes(term)
    );
  }, [approvedUsers, searchTerm]);

  const refreshUsers = async () => {
    const querySnapshot = await getDocs(collection(db, "deriv_users"));
    const allUsers = querySnapshot.docs.map((docSnap) => ({ uid: docSnap.id, ...docSnap.data() }));
    setUsers(allUsers);
  };

  const handleAction = async (uid, action, product = null) => {
    if (actionLoading[uid]) return;
    setActionLoading((prev) => ({ ...prev, [uid]: true }));

    try {
      let updateData;

      if (action === "reject") {
        updateData = {
          approved: false,
          status: "rejected",
          "approvals.matches": false,
          "approvals.overUnder": false,
          "approvals.evenOdd": false,
        };
      } else if (action === "approve" && product) {
        updateData = {
          [`approvals.${product}`]: true,
          status: "active",
          approvedAt: new Date().toISOString(),
        };
        if (product === "matches") {
          updateData.approved = true;
        }
      }

      if (!updateData) return;

      await updateDoc(doc(db, "deriv_users", uid), updateData);
      await refreshUsers();
    } catch (err) {
      console.error(`Admin ${action} error:`, err);
      const errorMessage =
        err.code === "permission-denied"
          ? "Permission denied. Check your admin role."
          : err.code === "not-found"
          ? "User not found."
          : `Action failed: ${err.message}`;
      alert(errorMessage);
    } finally {
      setActionLoading((prev) => ({ ...prev, [uid]: false }));
    }
  };

  const logout = () => {
    localStorage.removeItem("adminInfo");
    navigate("/admin/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f1514] text-slate-300">
        Loading Matches Pro admin dashboard...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1514] text-white">
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-6">
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#819C97]">Matches Pro admin</p>
            <h1 className="mt-2 text-4xl font-black text-[#A87F23]">Admin Dashboard</h1>
            <p className="mt-2 text-slate-400">
              Welcome back, <span className="font-semibold text-white">{adminName}</span>
            </p>
          </div>

          <button
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-medium transition hover:border-red-400/40 hover:text-red-300"
          >
            <FiLogOut /> Logout
          </button>
        </div>

        <div className="mb-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <StatCard label="Pending" value={pendingUsers.length} accent="text-[#A87F23]" />
          <StatCard label="Approved" value={approvedUsers.length} accent="text-[#819C97]" />
          <StatCard label="Total Users" value={users.length} accent="text-white" />
        </div>

        <div className="mb-8 flex flex-wrap gap-3 border-b border-white/10 pb-4">
          {[
            { key: "pending", label: `Pending (${pendingUsers.length})` },
            { key: "approved", label: `Approved (${approvedUsers.length})` },
            { key: "all", label: `All (${users.length})` },
            { key: "matchesTool", label: "Matches Preview", icon: true },
            { key: "overUnderTool", label: "Over/Under Preview", icon: true },
            { key: "evenOddTool", label: "Even/Odd Preview", icon: true },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-medium transition ${
                activeTab === key
                  ? "border-[#A87F23]/50 bg-[#A87F23]/15 text-[#f2d59a]"
                  : "border-white/10 bg-white/5 text-slate-300 hover:border-[#819C97]/40"
              }`}
            >
              {icon && <FiActivity size={14} />} {label}
            </button>
          ))}
        </div>

        {!["matchesTool", "overUnderTool", "evenOddTool"].includes(activeTab) && (
          <div className="relative mb-8 max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search by name, email or loginid..."
              className="w-full rounded-xl border border-white/10 bg-[#17201f] py-3 pl-11 pr-4 text-white placeholder-slate-500 focus:border-[#819C97] focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}

        <div className="min-h-[500px] rounded-[2rem] border border-white/10 bg-[#17201f] p-6">
          {activeTab === "pending" && renderUserGrid(filteredPendingUsers, true)}
          {activeTab === "approved" && renderUserGrid(filteredApprovedUsers, true)}
          {activeTab === "all" && renderUserGrid(filteredUsers, true)}
          {activeTab === "matchesTool" && <MatchesToolPreview />}
          {activeTab === "overUnderTool" && <OverUnderToolPreview />}
          {activeTab === "evenOddTool" && <EvenOddToolPreview />}
        </div>
      </div>
    </div>
  );

  function renderUserGrid(list, showActions = false) {
    if (list.length === 0) {
      return (
        <div className="py-16 text-center text-slate-400">
          <p className="text-xl">No users found</p>
          {searchTerm && <p className="mt-2">Try a different search term.</p>}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {list.map((user) => {
          const anyApproved = hasAnyApproval(user);

          return (
            <div
              key={user.uid}
              className="rounded-[1.5rem] border border-white/10 bg-[#0f1514] p-5 transition hover:border-[#819C97]/30"
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold text-white">{user.name || "Unnamed"}</p>
                  <p className="text-sm text-slate-400">{user.email}</p>
                  <p className="mt-1 text-xs text-slate-500">ID: {user.loginid}</p>
                </div>

                <div
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
                    anyApproved
                      ? "border border-[#819C97]/30 bg-[#819C97]/15 text-[#d0ddd9]"
                      : "border border-[#A87F23]/30 bg-[#A87F23]/15 text-[#f2d59a]"
                  }`}
                >
                  {anyApproved ? "Active" : "Pending"}
                </div>
              </div>

              <div className="mb-3 space-y-1 text-sm text-slate-400">
                <p>
                  Status: <span className="text-slate-200">{user.status}</span>
                </p>
                {user.approvedAt && (
                  <p>
                    Since: <span className="text-white">{new Date(user.approvedAt).toLocaleDateString()}</span>
                  </p>
                )}
              </div>

              {/* Product approval badges */}
              <div className="mb-4 flex flex-wrap gap-1.5">
                {PRODUCTS.map((p) => {
                  const approved = isProductApproved(user, p.key);
                  return (
                    <span
                      key={p.key}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        approved
                          ? "bg-[#819C97]/20 text-[#a8c4bf]"
                          : "bg-white/5 text-slate-500"
                      }`}
                    >
                      {approved ? "✓" : "–"} {p.label} {p.price}
                    </span>
                  );
                })}
              </div>

              {showActions && (
                <div className="space-y-2">
                  {/* Per-product approve buttons for unapproved products */}
                  <div className="flex flex-wrap gap-2">
                    {PRODUCTS.filter((p) => !isProductApproved(user, p.key)).map((p) => (
                      <button
                        key={p.key}
                        onClick={() => handleAction(user.uid, "approve", p.key)}
                        disabled={actionLoading[user.uid]}
                        className="flex items-center gap-1.5 rounded-xl bg-[#819C97]/20 px-3 py-2 text-xs font-medium text-[#a8c4bf] transition hover:bg-[#819C97]/35 disabled:opacity-50"
                      >
                        <FiCheckCircle size={12} /> {p.label} {p.price}
                      </button>
                    ))}
                  </div>

                  {/* Reject all */}
                  <button
                    onClick={() => handleAction(user.uid, "reject")}
                    disabled={actionLoading[user.uid]}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-700/20 py-2 text-xs font-medium text-red-400 transition hover:bg-red-700/35 disabled:opacity-50"
                  >
                    <FiXCircle size={12} /> Reject All
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-[1.75rem] border border-white/10 bg-[#17201f] p-6 text-center">
      <p className="mb-1 text-sm text-slate-400">{label}</p>
      <p className={`text-4xl font-black ${accent}`}>{value}</p>
    </div>
  );
}

function MatchesToolPreview() {
  const [markets, setMarkets] = useState([]);
  const [signals, setSignals] = useState({});
  const [phase, setPhase] = useState("countdown");
  const [timer, setTimer] = useState(15);
  const ticksRef = useRef({});
  const marketsRef = useRef([]);

  useEffect(() => {
    marketsRef.current = markets;
  }, [markets]);

  useEffect(() => {
    const ws = fetchVolatilityMarkets(setMarkets, (symbol, quote) => {
      const digit = Math.floor(quote * 10) % 10;
      const prev = ticksRef.current[symbol] || [];
      ticksRef.current[symbol] = [...prev, digit].slice(-50);
    });
    return () => ws.close();
  }, []);

  useEffect(() => {
    const generateAllSignals = () => {
      marketsRef.current.forEach(({ symbol }) => {
        const history = ticksRef.current[symbol];
        const recent = history && history.length > 0 ? history.slice(-50) : [Math.floor(Math.random() * 10)];
        const freq = Array(10).fill(0);
        recent.forEach((d) => freq[d]++);
        let maxCount = 0, bestDigit = 0;
        freq.forEach((count, digit) => { if (count > maxCount) { maxCount = count; bestDigit = digit; } });
        const confidence = scaleConfidence((maxCount / recent.length) * 100);
        setSignals((prev) => ({ ...prev, [symbol]: { digit: bestDigit, confidence } }));
      });
    };

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (phase === "countdown") { setPhase("entry"); generateAllSignals(); return 5; }
          if (phase === "entry") { setPhase("trade"); return 5; }
          if (phase === "trade") { setPhase("countdown"); return 15; }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  return (
    <div className="px-2 py-6 sm:px-4">
      <h2 className="mb-6 text-center text-3xl font-black text-[#A87F23]">Matches Pro Live Preview</h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {markets.map(({ name, symbol }) => {
          const signal = signals[symbol] || {};
          const showEntry = phase === "entry" && signal.digit !== undefined;
          const showTrade = phase === "trade" && signal.digit !== undefined;

          return (
            <div
              key={symbol}
              className="rounded-[1.5rem] border border-white/10 bg-[#0f1514] p-4 text-center transition hover:border-[#819C97]/30"
            >
              <h3 className="mb-3 truncate text-sm font-semibold text-[#d5dddb]">{name}</h3>

              {showEntry || showTrade ? (
                <>
                  <div className="mx-auto mb-2 flex h-20 w-20 items-center justify-center rounded-full bg-[#A87F23] text-3xl font-extrabold text-[#0f1514]">
                    {signal.digit}
                  </div>
                  <p className="mb-1 text-base font-semibold text-white">{signal.confidence}%</p>
                  {showEntry ? (
                    <p className="text-base font-medium text-[#A87F23]">Entry in {timer}s</p>
                  ) : (
                    <p className="text-xl font-black text-[#819C97]">TRADE</p>
                  )}
                </>
              ) : (
                <>
                  <div className="mx-auto mb-2 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-[#17201f] text-2xl font-bold text-slate-400">
                    {timer}s
                  </div>
                  <p className="text-sm text-slate-400">Next prediction</p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverUnderToolPreview() {
  const [markets, setMarkets] = useState([]);
  const [signals, setSignals] = useState({});
  const [phase, setPhase] = useState("countdown");
  const [timer, setTimer] = useState(15);
  const ticksRef = useRef({});
  const marketsRef = useRef([]);

  const computeOUSignal = (recent) => {
    const mean = recent.reduce((a, b) => a + b, 0) / recent.length;
    const deviation = Math.abs(mean - 4.5);
    const confidence = Math.min(99, Math.round(60 + (deviation / 4.5) * 39));
    let direction, barrier;
    if (mean >= 4.5) { direction = "OVER"; barrier = Math.min(8, Math.max(6, Math.round(mean + 1.5))); }
    else { direction = "UNDER"; barrier = Math.min(3, Math.max(1, Math.round(mean - 1.5))); }
    return { direction, barrier, confidence };
  };

  useEffect(() => {
    marketsRef.current = markets;
  }, [markets]);

  useEffect(() => {
    const ws = fetchVolatilityMarkets(setMarkets, (symbol, quote) => {
      const digit = Math.floor(quote * 10) % 10;
      const prev = ticksRef.current[symbol] || [];
      ticksRef.current[symbol] = [...prev, digit].slice(-50);
    });
    return () => ws.close();
  }, []);

  useEffect(() => {
    const generateAllSignals = () => {
      marketsRef.current.forEach(({ symbol }) => {
        const history = ticksRef.current[symbol];
        const recent = history && history.length > 0 ? history.slice(-50) : [Math.floor(Math.random() * 10)];
        setSignals((prev) => ({ ...prev, [symbol]: computeOUSignal(recent) }));
      });
    };
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (phase === "countdown") { setPhase("entry"); generateAllSignals(); return 5; }
          if (phase === "entry") { setPhase("trade"); return 5; }
          if (phase === "trade") { setPhase("countdown"); return 15; }
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  return (
    <div className="px-2 py-6 sm:px-4">
      <h2 className="mb-6 text-center text-3xl font-black text-[#A87F23]">Over/Under Pro Live Preview</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {markets.map(({ name, symbol }) => {
          const signal = signals[symbol] || {};
          const showSignal = (phase === "entry" || phase === "trade") && signal.direction !== undefined;
          return (
            <div key={symbol} className="rounded-[1.5rem] border border-white/10 bg-[#0f1514] p-4 text-center transition hover:border-[#819C97]/30">
              <h3 className="mb-3 truncate text-sm font-semibold text-[#d5dddb]">{name}</h3>
              {showSignal ? (
                <>
                  <div className={`mx-auto mb-2 flex h-20 w-20 flex-col items-center justify-center rounded-full ${signal.direction === "OVER" ? "bg-[#A87F23] text-[#0f1514]" : "bg-[#819C97] text-[#0f1514]"}`}>
                    <span className="text-xs font-black leading-none">{signal.direction}</span>
                    <span className="text-2xl font-black leading-none">{signal.barrier}</span>
                  </div>
                  <p className="mb-1 text-base font-semibold text-white">{signal.confidence}%</p>
                  {phase === "entry" && <p className="text-base font-medium text-[#A87F23]">Entry in {timer}s</p>}
                  {phase === "trade" && <p className="text-xl font-black text-[#819C97]">TRADE</p>}
                </>
              ) : (
                <>
                  <div className="mx-auto mb-2 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-[#17201f] text-2xl font-bold text-slate-400">{timer}s</div>
                  <p className="text-sm text-slate-400">Next Over/Under cycle</p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EvenOddToolPreview() {
  const [markets, setMarkets] = useState([]);
  const [signals, setSignals] = useState({});
  const [phase, setPhase] = useState("countdown");
  const [timer, setTimer] = useState(15);
  const ticksRef = useRef({});
  const marketsRef = useRef([]);

  const computeEOSignal = (recent) => {
    const evenCount = recent.filter((d) => d % 2 === 0).length;
    const dominance = Math.max(evenCount, recent.length - evenCount) / recent.length;
    const direction = evenCount >= recent.length - evenCount ? "EVEN" : "ODD";
    const confidence = Math.min(99, Math.round(60 + (dominance - 0.5) * 78));
    return { direction, confidence };
  };

  useEffect(() => {
    marketsRef.current = markets;
  }, [markets]);

  useEffect(() => {
    const ws = fetchVolatilityMarkets(setMarkets, (symbol, quote) => {
      const digit = Math.floor(quote * 10) % 10;
      const prev = ticksRef.current[symbol] || [];
      ticksRef.current[symbol] = [...prev, digit].slice(-50);
    });
    return () => ws.close();
  }, []);

  useEffect(() => {
    const generateAllSignals = () => {
      marketsRef.current.forEach(({ symbol }) => {
        const history = ticksRef.current[symbol];
        const recent = history && history.length > 0 ? history.slice(-50) : [Math.floor(Math.random() * 10)];
        setSignals((prev) => ({ ...prev, [symbol]: computeEOSignal(recent) }));
      });
    };
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (phase === "countdown") { setPhase("entry"); generateAllSignals(); return 5; }
          if (phase === "entry") { setPhase("trade"); return 5; }
          if (phase === "trade") { setPhase("countdown"); return 15; }
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  return (
    <div className="px-2 py-6 sm:px-4">
      <h2 className="mb-6 text-center text-3xl font-black text-[#A87F23]">Even/Odd Pro Live Preview</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {markets.map(({ name, symbol }) => {
          const signal = signals[symbol] || {};
          const showSignal = (phase === "entry" || phase === "trade") && signal.direction !== undefined;
          return (
            <div key={symbol} className="rounded-[1.5rem] border border-white/10 bg-[#0f1514] p-4 text-center transition hover:border-[#819C97]/30">
              <h3 className="mb-3 truncate text-sm font-semibold text-[#d5dddb]">{name}</h3>
              {showSignal ? (
                <>
                  <div className={`mx-auto mb-2 flex h-20 w-20 items-center justify-center rounded-full text-lg font-extrabold ${signal.direction === "EVEN" ? "bg-[#819C97] text-[#0f1514]" : "bg-[#A87F23] text-[#0f1514]"}`}>
                    {signal.direction}
                  </div>
                  <p className="mb-1 text-base font-semibold text-white">{signal.confidence}%</p>
                  {phase === "entry" && <p className="text-base font-medium text-[#A87F23]">Entry in {timer}s</p>}
                  {phase === "trade" && <p className="text-xl font-black text-[#819C97]">TRADE</p>}
                </>
              ) : (
                <>
                  <div className="mx-auto mb-2 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-[#17201f] text-2xl font-bold text-slate-400">{timer}s</div>
                  <p className="text-sm text-slate-400">Next Even/Odd cycle</p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
